import { useSelector } from 'react-redux';
import { ParamFunctionariesSwitchboard } from '../../../../../shared/types/param';
import { RootState } from '../../../../../redux/store';
import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';
import { FunctionariesSwitchboardListActions } from './actions';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { asyncGetFunctionariesSwitchFilters } from '../../../../../services/functionaries-switch.service';
import { FunctionariesSwitch } from '../../../../../shared/types/systemConfiguration/functionariesSwitch';

export const useFunctionariesSwitchboardList = (
    filters?: ParamFunctionariesSwitchboard
) => {
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<FunctionariesSwitch[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] =
        useState<ParamFunctionariesSwitchboard>({});
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<FunctionariesSwitch>[] = [];

    if (!isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <FunctionariesSwitchboardListActions
                    id={row.row.id || ''}
                    onDelete={() => setRefreshKey((k) => k + 1)}
                />
            ),
        });
    }

    columns.push(
        { field: 'name', headerName: 'Nombre', minWidth: 150, flex: 0.5 },
        { field: 'surname', headerName: 'Apellido', minWidth: 150, flex: 0.5 },

        {
            field: 'area',
            headerName: 'Área',
            minWidth: 150,
            flex: 0.6,
            valueGetter: (p) => {
                const a = p.row.area;
                return typeof a === 'string' ? '' : a?.name ?? '';
            },
        },
        {
            field: 'position',
            headerName: 'Cargo funcionario',
            minWidth: 180,
            flex: 0.8,
            valueGetter: (p) => {
                const pos = p.row.position;
                return typeof pos === 'string' ? '' : pos?.name ?? '';
            },
        },

        {
            field: 'email',
            headerName: 'Correo electrónico',
            minWidth: 220,
            flex: 1,
        },

        {
            field: 'active',
            headerName: 'Activo',
            minWidth: 80,
            flex: 0.4,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <>{row.row.active ? 'Sí' : 'No'}</>,
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        },
        {
            field: 'updated_at',
            headerName: 'Fecha última modificación',
            minWidth: 120,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.updated_at} />,
        },
        {
            field: 'updatedBy',
            headerName: 'Última modificación por',
            minWidth: 180,
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (p) =>
                `${p.row.updatedBy?.name ?? ''} ${
                    p.row.updatedBy?.surname ?? ''
                }`.trim(),
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);

        asyncGetFunctionariesSwitchFilters(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters, refreshKey]);

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
